<template>
  <div class="mt-6">
    <div class="border border-gray-200 rounded-lg overflow-hidden text-sm">
      <!-- Header -->
      <div class="flex bg-gray-50 border-b border-gray-200">
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Athletic Info
        </div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">Value</div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Last Updated
        </div>
      </div>
      <!-- Primary Position (Baseball) -->
      <PlayerInfoRow
        :label="readModel.primaryPositionBaseball.label"
        :last-updated="formRows.primaryPositionBaseball.lastUpdated"
        :display="readModel.primaryPositionBaseball.display"
        :is-editing="isEditing"
        data-testid="primary-position-baseball-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.primaryPositionBaseball.data"
            :options="formRows.primaryPositionBaseball.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.primaryPositionBaseball.data }}
        </template>
      </PlayerInfoRow>
      <!-- Secondary Position (Baseball) -->
      <PlayerInfoRow
        :label="readModel.secondaryPositionBaseball.label"
        :last-updated="formRows.secondaryPositionBaseball.lastUpdated"
        :display="readModel.secondaryPositionBaseball.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="secondary-position-baseball-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.secondaryPositionBaseball.data"
            :options="formRows.secondaryPositionBaseball.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.secondaryPositionBaseball.data }}
        </template>
      </PlayerInfoRow>
      <!-- Primary Position (Volleyball) -->
      <PlayerInfoRow
        :label="readModel.primaryPositionVolleyball.label"
        :last-updated="formRows.primaryPositionVolleyball.lastUpdated"
        :display="readModel.primaryPositionVolleyball.display"
        :is-editing="isEditing"
        data-testid="primary-position-volleyball-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.primaryPositionVolleyball.data"
            :options="formRows.primaryPositionVolleyball.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.primaryPositionVolleyball.data }}
        </template>
      </PlayerInfoRow>
      <!-- Secondary Position (Volleyball) -->
      <PlayerInfoRow
        :label="readModel.secondaryPositionVolleyball.label"
        :last-updated="formRows.secondaryPositionVolleyball.lastUpdated"
        :display="readModel.secondaryPositionVolleyball.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="secondary-position-volleyball-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.secondaryPositionVolleyball.data"
            :options="formRows.secondaryPositionVolleyball.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.secondaryPositionVolleyball.data }}
        </template>
      </PlayerInfoRow>
      <!-- Handedness -->
      <PlayerInfoRow
        :label="readModel.handedness.label"
        :last-updated="formRows.handedness.lastUpdated"
        :display="readModel.handedness.display"
        :is-editing="isEditing"
        data-testid="handedness-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.handedness.data"
            :options="formRows.handedness.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.handedness.data }}
        </template>
      </PlayerInfoRow>
      <!-- Throws -->
      <PlayerInfoRow
        :label="readModel.throws.label"
        :last-updated="formRows.throws.lastUpdated"
        :display="readModel.throws.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="throws-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.throws.data"
            :options="formRows.throws.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.throws.data }}
        </template>
      </PlayerInfoRow>
      <!-- Bats -->
      <PlayerInfoRow
        :label="readModel.bats.label"
        :last-updated="formRows.bats.lastUpdated"
        :display="readModel.bats.display"
        :is-editing="isEditing"
        data-testid="bats-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.bats.data"
            :options="formRows.bats.values"
            class="w-full"
          />
        </template>
        <template #display>{{ formRows.bats.data }}</template>
      </PlayerInfoRow>
      <!-- Skill Level -->
      <PlayerInfoRow
        :label="readModel.skillLevel.label"
        :last-updated="formRows.skillLevel.lastUpdated"
        :display="readModel.skillLevel.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="skill-level-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.skillLevel.data"
            :options="formRows.skillLevel.values"
            class="w-full"
          />
        </template>
        <template #display>{{ formRows.skillLevel.data }}</template>
      </PlayerInfoRow>
      <!-- Jersey Top Size -->
      <PlayerInfoRow
        :label="readModel.jerseyTopSize.label"
        :last-updated="formRows.jerseyTopSize.lastUpdated"
        :display="readModel.jerseyTopSize.display"
        :is-editing="isEditing"
        data-testid="jersey-top-size-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.jerseyTopSize.data"
            :options="formRows.jerseyTopSize.values"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </template>
        <template #display>
          {{ jerseySizeLabel(formRows.jerseyTopSize.data) }}
        </template>
      </PlayerInfoRow>
      <!-- Jersey Bottom Size -->
      <PlayerInfoRow
        :label="readModel.jerseyBottomSize.label"
        :last-updated="formRows.jerseyBottomSize.lastUpdated"
        :display="readModel.jerseyBottomSize.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="jersey-bottom-size-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.jerseyBottomSize.data"
            :options="formRows.jerseyBottomSize.values"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </template>
        <template #display>
          {{ jerseySizeLabel(formRows.jerseyBottomSize.data) }}
        </template>
      </PlayerInfoRow>
      <!-- 1st Choice Jersey Number -->
      <PlayerInfoRow
        :label="readModel.firstChoiceJerseyNumber.label"
        :last-updated="formRows.firstChoiceJerseyNumber.lastUpdated"
        :display="readModel.firstChoiceJerseyNumber.display"
        :is-editing="isEditing"
        data-testid="first-choice-jersey-number-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputNumber
            v-model="formRows.firstChoiceJerseyNumber.data"
            :min="0"
            :max="99"
            class="w-full input-number-fix"
            show-buttons
          />
        </template>
        <template #display>
          {{ formRows.firstChoiceJerseyNumber.data }}
        </template>
      </PlayerInfoRow>
      <!-- 2nd Choice Jersey Number -->
      <PlayerInfoRow
        :label="readModel.secondChoiceJerseyNumber.label"
        :last-updated="formRows.secondChoiceJerseyNumber.lastUpdated"
        :display="readModel.secondChoiceJerseyNumber.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="second-choice-jersey-number-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputNumber
            v-model="formRows.secondChoiceJerseyNumber.data"
            :min="0"
            :max="99"
            class="w-full input-number-fix"
            show-buttons
          />
        </template>
        <template #display>
          {{ formRows.secondChoiceJerseyNumber.data }}
        </template>
      </PlayerInfoRow>
      <!-- Hat Size -->
      <PlayerInfoRow
        :label="readModel.hatSize.label"
        :last-updated="formRows.hatSize.lastUpdated"
        :display="readModel.hatSize.display"
        :is-editing="isEditing"
        data-testid="hat-size-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <Select
            v-model="formRows.hatSize.data"
            :options="formRows.hatSize.values"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.hatSize.data }}
        </template>
      </PlayerInfoRow>
      <!-- Hat Size (Numbered) -->
      <PlayerInfoRow
        :label="readModel.hatSizeNumbered.label"
        :last-updated="formRows.hatSizeNumbered.lastUpdated"
        :display="readModel.hatSizeNumbered.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="hat-size-numbered-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <HatSizeNumberedInput
            v-model="formRows.hatSizeNumbered.data"
            :min="5"
            :max="10"
          />
        </template>
        <template #display>
          {{ formatHatSizeNumbered(formRows.hatSizeNumbered.data) }}
        </template>
      </PlayerInfoRow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Select from "primevue/select";
import InputNumber from "primevue/inputnumber";
import HatSizeNumberedInput from "./HatSizeNumberedInput.vue";
import PlayerInfoRow from "../PlayerInfoRow.vue";
import type { AthleticInfoFormRows } from "./types";
import { formatHatSizeNumbered } from "@/modules/profile/utils/formatters";
import { JERSEY_SIZE_LABELS } from "@/modules/profile/models/AthleticInfo";
import type { JerseySizeEnum } from "@/api/model";
import * as filter from "../filter";

const props = defineProps<{
  isEditing: boolean;
  searchQuery: string | null | undefined;
}>();

const formRows = defineModel<AthleticInfoFormRows>("formRows", {
  required: true,
});

const jerseySizeLabel = (value: JerseySizeEnum | "" | undefined) => {
  if (!value) return value;
  return JERSEY_SIZE_LABELS[value] ?? value;
};

defineEmits<{
  enterEditMode: [];
}>();

const readModel = computed(() => {
  return {
    primaryPositionBaseball: {
      label: "Primary Position (Baseball)",
      display: filter.matches(
        "Primary Position (Baseball)",
        formRows.value?.primaryPositionBaseball.data,
        props.searchQuery
      ),
    },
    secondaryPositionBaseball: {
      label: "Secondary Position (Baseball)",
      display: filter.matches(
        "Secondary Position (Baseball)",
        formRows.value?.secondaryPositionBaseball.data,
        props.searchQuery
      ),
    },
    primaryPositionVolleyball: {
      label: "Primary Position (Volleyball)",
      display: filter.matches(
        "Primary Position (Volleyball)",
        formRows.value?.primaryPositionVolleyball.data,
        props.searchQuery
      ),
    },
    secondaryPositionVolleyball: {
      label: "Secondary Position (Volleyball)",
      display: filter.matches(
        "Secondary Position (Volleyball)",
        formRows.value?.secondaryPositionVolleyball.data,
        props.searchQuery
      ),
    },
    handedness: {
      label: "Handedness",
      display: filter.matches(
        "Handedness",
        formRows.value?.handedness.data,
        props.searchQuery
      ),
    },
    throws: {
      label: "Throws",
      display: filter.matches(
        "Throws",
        formRows.value?.throws.data,
        props.searchQuery
      ),
    },
    bats: {
      label: "Bats",
      display: filter.matches(
        "Bats",
        formRows.value?.bats.data,
        props.searchQuery
      ),
    },
    skillLevel: {
      label: "Skill Level",
      display: filter.matches(
        "Skill Level",
        formRows.value?.skillLevel.data,
        props.searchQuery
      ),
    },
    jerseyTopSize: {
      label: "Jersey Top Size",
      display: filter.matches(
        "Jersey Top Size",
        formRows.value?.jerseyTopSize.data,
        props.searchQuery
      ),
    },
    jerseyBottomSize: {
      label: "Jersey Bottom Size",
      display: filter.matches(
        "Jersey Bottom Size",
        formRows.value?.jerseyBottomSize.data,
        props.searchQuery
      ),
    },
    firstChoiceJerseyNumber: {
      label: "1st Choice Jersey Number",
      display: filter.matches(
        "1st Choice Jersey Number",
        formRows.value?.firstChoiceJerseyNumber.data?.toString(),
        props.searchQuery
      ),
    },
    secondChoiceJerseyNumber: {
      label: "2nd Choice Jersey Number",
      display: filter.matches(
        "2nd Choice Jersey Number",
        formRows.value?.secondChoiceJerseyNumber.data?.toString(),
        props.searchQuery
      ),
    },
    hatSize: {
      label: "Hat Size",
      display: filter.matches(
        "Hat Size",
        formRows.value?.hatSize.data,
        props.searchQuery
      ),
    },
    hatSizeNumbered: {
      label: "Hat Size (Numbered)",
      display: filter.matches(
        "Hat Size (Numbered)",
        formatHatSizeNumbered(formRows.value?.hatSizeNumbered.data),
        props.searchQuery
      ),
    },
  };
});
</script>
<style scoped>
.input-number-fix :deep(input) {
  width: 100%;
}
.input-number-fix :deep(span[data-pc-section="buttongroup"]) {
  z-index: 11;
}
</style>
